import { combineReducers } from 'redux';
import { createReducer } from 'typesafe-actions';

import { fetchCouponListAsync } from 'store/furusatoNozei/actions';
import { fetchMobileCouponListAsync } from 'store/rakutenMobile/actions';

import { CouponStatus, CouponStatusNumber } from 'constants/reservations';
import type { ReservationDetails } from 'ReservationsDetails-Types';

import {
  fetchMoreReservationListAsync,
  fetchReservationsListAsync,
} from '../reservationsList/actions';
import { fetchBookingDetailAsync } from './actions';

const reduceDomesticDetails = (acc, reservation) => {
  const reservationToAdd =
    reservation.serviceType === 'DOMESTIC'
      ? {
          [reservation.reservationId]: {
            ...reservation,
          },
        }
      : {};
  return {
    ...acc,
    ...reservationToAdd,
  };
};

const getDetailObject = (state, bookingId) => {
  const previousDetail = state?.[bookingId];
  return {
    ...(typeof previousDetail === 'object' ? previousDetail : {}),
  };
};

const getCouponStatue = (status) => {
  switch (status) {
    case CouponStatusNumber.APPLICABLE:
      return CouponStatus.APPLICABLE;
    case CouponStatusNumber.ALREADY_APPLIED:
      return CouponStatus.ALREADY_APPLIED;
    case CouponStatusNumber.NOT_APPLICABLE:
      return CouponStatus.NOT_APPLICABLE;
    default:
      return CouponStatus.DISABLE;
  }
};

export const isFetchingDetails = createReducer(false as boolean)
  .handleAction([fetchBookingDetailAsync.request], () => true)
  .handleAction([fetchBookingDetailAsync.success, fetchBookingDetailAsync.failure], () => false);

export const reservationDetail = createReducer({} as ReservationDetails)
  .handleAction(fetchBookingDetailAsync.success, (state, action) => {
    const bookingDetail = action.payload.booking[0];

    return {
      ...state,
      [bookingDetail.bookingId]: {
        ...getDetailObject(state, bookingDetail.bookingId),
        ...bookingDetail,
        hometownTaxCouponStatus: getCouponStatue(bookingDetail.hometownTaxCouponStatus),
        mobileOnlyCouponStatus: getCouponStatue(bookingDetail.mobileOnlyCouponStatus),
      },
    };
  })
  .handleAction(fetchReservationsListAsync.success, (state, action) => {
    const newState = action.payload.reservations.reduce(
      reduceDomesticDetails,
      {} as ReservationDetails,
    );

    return { ...state, ...newState };
  })
  .handleAction(fetchMoreReservationListAsync.success, (state, action) => ({
    ...state,
    ...action.payload.reservations.reduce(reduceDomesticDetails, {} as ReservationDetails),
  }))
  .handleAction(fetchCouponListAsync.failure, (state, action) => {
    const payload = action.payload;
    const newReservations = state[payload.reservationId]
      ? {
          [payload.reservationId]: {
            ...getDetailObject(state, payload.reservationId),
            hometownTaxCouponStatus: CouponStatus.DISABLE,
          },
        }
      : {};

    return { ...state, ...newReservations };
  })
  .handleAction(fetchMobileCouponListAsync.failure, (state, action) => {
    const payload = action.payload;
    const newReservations = state[payload.reservationId]
      ? {
          [payload.reservationId]: {
            ...getDetailObject(state, payload.reservationId),
            mobileOnlyCouponStatus: CouponStatus.DISABLE,
          },
        }
      : {};

    return { ...state, ...newReservations };
  });

const reservationDetailError = createReducer(null as null | Error)
  .handleAction(fetchBookingDetailAsync.request, () => null)
  .handleAction(fetchBookingDetailAsync.failure, (_state, action) => action.payload);

const reservationDetailsReducer = combineReducers({
  reservationDetail,
  isFetchingDetails,
  reservationDetailError,
});

export default reservationDetailsReducer;
export type ReservationDetailsState = ReturnType<typeof reservationDetailsReducer>;
